import React, { useEffect, useState, ReactNode, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { isEmpty, isNumber, round } from 'lodash';
import { init } from 'echarts';

import Color from '@tool/color';
import moDemandPdf from '../store';
import moIndex from '../../store';

interface IProps {
  cityActive: string;
  list: any;
  index?: number;
  show?: boolean;
}
interface IState {}

const CityChart = (props: IProps, state: IState) => {
  let chart: any = null;
  const navi = useNavigate();
  const cityRef = useRef<any>(null);
  const { cityActive, list, index, show } = props;
  const colorList = [Color.cf, Color.primary, Color.secondary];
  const { isMock } = moIndex;
  const { cityRecruitList } = moDemandPdf;

  const getMax = useMemo(() => {
    let max1: any = 0;
    let max2: any = 0;
    let min:any = 0;
    if (!isEmpty(cityRecruitList) && !isEmpty(cityRecruitList.data)) {
      const yData1: any = [];
      const yData2: any = [];
      if (cityActive === 'num' ) {
        cityRecruitList.data.forEach((res: any) => {
          yData1.push(res.amount_previous);
          yData1.push(res.amount_current);
          yData2.push(res.amount_change);
        });
      }
      if (cityActive === 'salary') {
        cityRecruitList.data.forEach((res: any) => {
          yData1.push(res.salary_previous);
          yData1.push(res.salary_current);
          yData2.push(res.salary_change);
        });
      }

      const res: any = yData1.sort((a: any, b: any) => b - a);
      const res1: any = yData2.sort((a: any, b: any) => b - a);
      max1 = res[0];
      max2 = res1[0];
      min = res1[res1.length - 1];
    }
    return { max1, max2, min };
  }, [cityRecruitList]);

  const getResult = useMemo(() => {
    const xData: any = [];
    const yData1: any = [];
    const yData2: any = [];
    const yData3: any = [];

    if (cityActive === 'num' && !isEmpty(list)) {
      list.forEach((res: any) => {
        xData.push(res.city);
        yData1.push(res.amount_previous);
        yData2.push(res.amount_current);
        yData3.push(res.amount_change);
      });
    }
    if (cityActive === 'salary' && !isEmpty(list)) {
      list.forEach((res: any) => {
        xData.push(res.city);
        yData1.push(res.salary_previous);
        yData2.push(res.salary_current);
        yData3.push(res.salary_change);
      });
    }
    return { xData, yData1, yData2, yData3 };
  }, [cityActive, list]);

  const render = () => {
    if (chart) chart.dispose();
    chart = init(cityRef.current);
    const option = {
      color: colorList,
      legend: {
        data:
          cityActive === 'num' ?
            ['上期招聘量', '当期招聘量', '招聘量环比变化率'] :
            [
              '上期平均月薪（元）',
              '当期平均月薪（元）',
              '平均月薪环比变化率',
            ],
        bottom: 10,
        show: show,
        icon: 'roundRect',
        selectedMode: false,
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          color: Color.c87,
          fontSize: 12,
        },
      },
      grid: {
        left: 10,
        right: 10,
        bottom: show ? 40 : 10,
        top: 30,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: getResult.xData,
        axisTick: { show: false },
        axisLine: {
          lineStyle: { color: Color.ccc },
        },
        axisLabel: {
          color: Color.blackColor(0.85),
          fontSize: 12,
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLine: {
            show: false,
            lineStyle: { color: Color.ccc },
          },
          max: getMax.max1,
          axisTick: { show: false },
          axisLabel: {
            show: true,
            formatter: (val: number) => {
              if (val === 0) return 0;
              return `${Number(val).toLocaleString()}`;
            },
            fontSize: 12,
            color: Color.blackColor(0.5),
            fontFamily: 'Roboto',
            showMaxLabel: false,
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: Color.blackColor(0.08),
              width: 1,
              type: 'dashed',
            },
          },
        },
        {
          type: 'value',
          axisLine: {
            show: false,
            lineStyle: { color: Color.ccc },
          },
          max: getMax.max2,
          min:getMax.min,
          axisTick: { show: false },
          axisLabel: {
            fontFamily: 'Roboto',
            show: true,
            formatter: (val: number) => {
              if (val === 0) return 0;
              return `${round(Number(val) * 100, 0)}%`;
            },
            fontSize: 12,
            color: Color.blackColor(0.5),
            showMaxLabel: false,
          },
          splitLine: {
            show: false,
            lineStyle: {
              color: Color.blackColor(0.08),
              width: 1,
              type: 'dashed',
            },
          },
        },
      ],
      series: [
        {
          name: cityActive === 'num' ? '上期招聘量' : '上期平均月薪（元）',
          type: 'bar',
          barWidth: 10,
          data: getResult.yData1,
          itemStyle: {
            borderRadius: [2, 2, 0, 0],
          },
        },
        {
          name: cityActive === 'num' ? '当期招聘量' : '当期平均月薪（元）',
          type: 'bar',
          barWidth: 10,
          itemStyle: {
            borderRadius: [2, 2, 0, 0],
          },
          data: getResult.yData2,
        },
        {
          name:
            cityActive === 'num' ? '招聘量环比变化率' : '平均月薪环比变化率',
          type: 'line',
          yAxisIndex: 1,
          label: {
            formatter(parmars: any) {
              return `${round(parmars.value * 100, 1)}%`;
            },
            show: true,
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: Color.secondary,
          },
          data: getResult.yData3,
        },
      ],
    };
    chart.setOption(option, true);
  };

  useEffect(() => {
    render();
    window.addEventListener('resize', render);
    return () => {
      window.removeEventListener('resize', render);
    };
  }, [getResult]);

  return (
    <section
      className="city-svg-warp"
      style={{ borderRadius: index === 0 ? '0' : '0 0 5px 5px' }}>
      <section
        className="city-svg"
        ref={cityRef}
        id="citysvg"
        style={{
          backgroundImage: isMock ?
            'url(https://file.smartsalary.cn/web-img%402.0/market/image/ismock.png)' :
            '',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
        }}></section>
    </section>
  );
};

export default observer(CityChart);
